import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import type { LinkButtonsWidgetParams } from '@/types/chatbot.types';
import LinkButtonText from './LinkButtonText';
import * as S from './widgets.styles';

const LinkButtonsWidget: React.FC<LinkButtonsWidgetParams> = observer(
  ({ linkButtons, onAnimationComplete, shouldStart = true }) => {
    const navigate = useNavigate();
    const [completedButtons, setCompletedButtons] = useState<Set<number>>(new Set());
    const completedButtonsRef = useRef<Set<number>>(new Set());

    // Reset completed buttons when shouldStart changes or linkButtons change
    useEffect(() => {
      if (!shouldStart) {
        // If animation disabled, mark all as completed immediately
        const allCompleted = new Set(linkButtons?.map((_, i) => i) || []);
        setCompletedButtons(allCompleted);
        completedButtonsRef.current = allCompleted;
      } else {
        // Reset when animation starts
        setCompletedButtons(new Set());
        completedButtonsRef.current = new Set();
      }
    }, [shouldStart, linkButtons]);

    // Track when all buttons have finished animating
    useEffect(() => {
      if (
        shouldStart &&
        linkButtons &&
        linkButtons.length > 0 &&
        completedButtonsRef.current.size === linkButtons.length &&
        onAnimationComplete
      ) {
        onAnimationComplete();
      }
    }, [linkButtons, completedButtons, onAnimationComplete, shouldStart]);

    const handleButtonComplete = (index: number) => {
      setCompletedButtons(prev => {
        const newSet = new Set(prev);
        newSet.add(index);
        completedButtonsRef.current = newSet;
        return newSet;
      });
    };

    const handleLinkClick = (e: React.MouseEvent, url: string) => {
      e.preventDefault();

      // Check if it's an internal route or external URL
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    };

    if (!linkButtons || linkButtons.length === 0) {
      return null;
    }

    return (
      <S.LinkButtonsWidgetContainer>
        {linkButtons.map((button, index) => {
          // Sequential animation: first button starts immediately, others wait for previous to complete
          const shouldAnimateButton = shouldStart && (index === 0 || completedButtons.has(index - 1));

          // Only render button when it's its turn to animate or when animation is disabled
          // This prevents buttons from showing before their animation starts
          const shouldShowButton = !shouldStart || shouldAnimateButton || completedButtons.has(index);

          if (!shouldShowButton) {
            return null;
          }

          return (
            <S.LinkButton key={index} href={button.redirectUrl} onClick={e => handleLinkClick(e, button.redirectUrl)}>
              <LinkButtonText
                content={button.urlText}
                shouldStart={shouldAnimateButton}
                onAnimationComplete={() => handleButtonComplete(index)}
              />
            </S.LinkButton>
          );
        })}
      </S.LinkButtonsWidgetContainer>
    );
  },
);

LinkButtonsWidget.displayName = 'LinkButtonsWidget';

export default LinkButtonsWidget;
